import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ProductoEditorDialogComponent } from '../producto-editor-dialog/producto-editor-dialog.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProductosService } from 'src/app/main/backoffice/modules/productos/services/productos.service';
import { Categoria } from 'src/app/main/backoffice/modules/productos/models/categoria.model';
import { Producto } from 'src/app/main/backoffice/modules/productos/models/producto.model';
import {
  BehaviorSubject,
  catchError,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { CartItem } from '../../../../models/cartItem.model';
import { ParametrosService } from 'src/app/main/backoffice/modules/parametros/services/parametros.service';
import { PedidosService } from 'src/app/main/backoffice/modules/pedidos/services/pedidos.service';
import { PedidoConfirmadoDialogComponentComponent } from '../../../../components/pedido-confirmado-dialog-component/pedido-confirmado-dialog-component.component';

@Component({
  selector: 'app-menu-tabs',
  templateUrl: './menu-tabs.component.html',
  styleUrls: ['./menu-tabs.component.scss'],
})
export class MenuTabsComponent implements OnInit {
  public checkoutForm: FormGroup;
  public confirmandoPedido: boolean = false;
  private imagenCache = new Map<number, string>();
  public cantidadCarrito = 0;
  private productosPorCategoria$ = new Map<number, Observable<Producto[]>>();
  public categoriaSeleccionada$ = new BehaviorSubject<Categoria | null>(null);
  public productos$!: Observable<Producto[]>;
  public productosPorCategoria = new Map<number, Producto[]>();
  public categorias: Categoria[] = [];
  public loadingCategorias = true;
  public loadingProductos = false;
  public imagenesProductos = new Map<number, string>();
  public tieneDelivery: boolean = false;
  public cartOpen: boolean = false;
  public carrito: CartItem[] = [];

  public formAbierto: boolean = false;
  public cartAnimation: boolean = false;

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private productosService: ProductosService,
    private parametrosService: ParametrosService,
    private pedidosService: PedidosService,
  ) {
    this.checkoutForm = this.fb.group({
      nombre: ['', Validators.required],
      telefono: ['', Validators.required],
      tipoEntrega: ['local', Validators.required],
      direccion: [''],
      metodoPago: ['efectivo', Validators.required],
      notas: [''],
    });

    this.checkoutForm.get('tipoEntrega')?.valueChanges.subscribe((tipo) => {
      const direccionCtrl = this.checkoutForm.get('direccion');

      if (tipo === 'delivery') {
        direccionCtrl?.setValidators(Validators.required);
      } else {
        direccionCtrl?.clearValidators();
        direccionCtrl?.setValue('');
      }

      direccionCtrl?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.cargarCategorias();
    this.obtenerParametros();
  }

  ngOnDestroy(): void {
    this.imagenesProductos.forEach((url) => URL.revokeObjectURL(url));
  }

  get totalPedido(): number {
    return this.carrito.reduce((acc, item) => acc + item.total, 0);
  }

  private obtenerParametros(): void {
    this.parametrosService.obtenerParametros().subscribe((params) => {
      this.tieneDelivery =
        params.find((p) => p.key === 'delivery_enabled')?.value === 'true';
    });
  }

  public toggleForm() {
    this.formAbierto = !this.formAbierto;
  }

  private obtenerProductosStream(categoryId: number): Observable<Producto[]> {
    const cached$ = this.productosPorCategoria$.get(categoryId);
    if (cached$) {
      return cached$; // ✅ NO vuelve a llamar al backend
    }

    const stream$ = this.productosService
      .obtenerProductos({
        categoryId,
        isAvailable: true,
      })
      .pipe(
        switchMap((productos) => {
          if (!productos.length) return of([]);

          return forkJoin(
            productos.map((producto) =>
              this.obtenerImagenCached(producto.id).pipe(
                map((image) => ({
                  ...producto,
                  imageUrl: image,
                })),
              ),
            ),
          );
        }),
        shareReplay(1), // 🔥 CLAVE
      );

    this.productosPorCategoria$.set(categoryId, stream$);
    return stream$;
  }

  private obtenerImagenCached(productoId: number): Observable<string> {
    const cached = this.imagenCache.get(productoId);

    if (cached) {
      return of(cached); // ✅ reutiliza
    }

    return this.productosService.obtenerImagenProducto(productoId).pipe(
      map((blob) => new Blob([blob], { type: 'image/jpeg' })),
      switchMap((blob) => this.blobToBase64(blob)),
      tap((image) => this.imagenCache.set(productoId, image)),
      catchError(() => of('assets/images/no-image.png')),
    );
  }

  cargarImagenProducto(productoId: number): void {
    if (this.imagenesProductos.has(productoId)) return;

    this.productosService.obtenerImagenProducto(productoId).subscribe({
      next: (blob) => {
        const imageBlob = new Blob([blob], { type: 'image/jpeg' });

        const imageUrl = URL.createObjectURL(imageBlob);
        this.imagenesProductos.set(productoId, imageUrl);
      },
      error: () => {
        this.imagenesProductos.set(productoId, 'assets/images/no-image.png');
      },
    });
  }

  obtenerImagen(productoId: number): string {
    return (
      this.imagenesProductos.get(productoId) ??
      'assets/images/loading-image.png'
    );
  }

  private cargarCategorias(): void {
    this.productosService.obtenerCategorias().subscribe({
      next: (cats) => {
        this.categorias = cats.filter((c) => c.isActive);
        this.loadingCategorias = false;

        if (this.categorias.length) {
          this.productos$ = this.obtenerProductosStream(this.categorias[0].id);
        }
      },
      error: () => {
        this.loadingCategorias = false;
      },
    });
  }

  onTabChange(index: number): void {
    const categoria = this.categorias[index];
    if (categoria) {
      this.productos$ = this.obtenerProductosStream(categoria.id);
    }
  }

  obtenerProductosCategoria(categoryId: number): Producto[] {
    return this.productosPorCategoria.get(categoryId) ?? [];
  }

  irAlCarrito() {
    this.cartOpen = true;
  }

  abrirCarrito() {
    this.cartOpen = true;
  }

  agregarAlCarrito(producto: Producto) {
    const nuevoItem: CartItem = {
      productoId: producto.id,
      nombre: producto.name,
      precioBase: producto.price,
      cantidad: 1,
      extras: [],
      total: producto.price,
      imageUrl: producto.imageUrl,
      categoriaID: producto.categoryId,
    };
    this.carrito.push(nuevoItem);
    producto.agregadoTemporal = true;

    this.triggerCartAnimation();

    setTimeout(() => {
      producto.agregadoTemporal = false;
    }, 2000);
    this.actualizarCantidadCarrito();
  }

  private actualizarCantidadCarrito() {
    this.cantidadCarrito = this.carrito.reduce(
      (acc, item) => acc + item.cantidad,
      0,
    );
  }

  editarItem(index: number) {
    const item = this.carrito[index];
    this.cartOpen = false;
    setTimeout(() => {
      this.editarProducto(item, index);
    }, 300);
  }

  editarProducto(item: any, index: number) {
    const dialog = this.dialog.open(ProductoEditorDialogComponent, {
      data: { ...item },
      width: '700px',
    });

    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.carrito[index] = res;
      }
    });
  }

  trackByProducto(_: number, p: Producto) {
    return p.id;
  }

  trackByCategoria(_: number, c: Categoria) {
    return c.id;
  }

  confirmarPedido(): void {
    if (this.checkoutForm.invalid || this.carrito.length === 0) return;
    const form = this.checkoutForm.value;
    console.log(this.carrito);

    const payload = {
      customerName: form.nombre,
      customerPhone: form.telefono,
      deliveryType: form.tipoEntrega === 'delivery' ? 2 : 1,
      deliveryAddress: form.tipoEntrega === 'delivery' ? form.direccion : null,
      deliveryNotes: form.notas || null,
      paymentMethod: this.mapearMetodoPago(form.metodoPago),
      deliveryFee: 0,
      items: this.expandirCarrito(this.carrito),
    };

    this.pedidosService.crearPedido(payload).subscribe({
      next: () => {
        this.dialog.open(PedidoConfirmadoDialogComponentComponent, {
          width: '360px',
          disableClose: true,
          data: {
            pedido: payload,
            carrito: this.carrito,
          },
        });
        this.cantidadCarrito = 0;
        this.carrito = [];
        this.checkoutForm.reset();
        this.cartOpen = false;
      },
      error: (err) => console.error(err),
    });
  }

  blobToBase64(blob: Blob): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = (err) => observer.error(err);
      reader.readAsDataURL(blob);
    });
  }

  eliminarItem(index: number): void {
    this.carrito.splice(index, 1);
    this.cantidadCarrito = this.carrito.reduce(
      (acc, item) => acc + item.cantidad,
      0,
    );
  }

  private expandirCarrito(carrito: CartItem[]): any[] {
    const items: any[] = [];
    carrito.forEach((item) => {
      for (let i = 0; i < item.cantidad; i++) {
        items.push({
          productId: item.productoId,
          notes: null,
          toppings: item.extras.map((extra) => ({
            toppingId: extra.id,
            quantity: 1,
          })),
        });
      }
    });

    return items;
  }

  private mapearMetodoPago(metodo: string): number {
    switch (metodo) {
      case 'efectivo':
        return 1;
      case 'tarjeta':
        return 2;
      case 'qr':
        return 3;
      case 'transferencia':
        return 4;
      default:
        return 1;
    }
  }

  private triggerCartAnimation(): void {
    this.cartAnimation = true;

    setTimeout(() => {
      this.cartAnimation = false;
    }, 1200);
  }

  private enviarPedidoWhatsApp(payload: any): void {
    const telefonoNegocio = '549XXXXXXXXXX'; // 👈 tu número con código país (Argentina +54)

    const detalleItems = payload.items
      .map((item: any) => `• ${item.quantity}x ${item.productName}`)
      .join('\n');

    const mensaje = `🧾 *Nuevo pedido recibido*

👤 Cliente: ${payload.customerName}
📞 Teléfono: ${payload.customerPhone}
🚚 Entrega: ${payload.deliveryType === 2 ? 'Delivery' : 'Retiro'}

${payload.deliveryAddress ? `📍 Dirección: ${payload.deliveryAddress}` : ''}

🍔 Pedido:
${detalleItems}

💳 Pago: ${payload.paymentMethod}
📝 Notas: ${payload.deliveryNotes ?? 'Ninguna'}
`;

    const url = `https://wa.me/${telefonoNegocio}?text=${encodeURIComponent(mensaje)}`;

    window.open(url, '_blank');
  }
}
